import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/auth/AuthProvider'
import { PERM } from '@/shared/auth/types'
import { getConfig } from '@/shared/config'
import { cn } from '@/shared/ui/primitives'
import { Icon, type IconName } from './icons'
import { buildCommands, documentJump, documentSearch, rankCommands } from './commands'

/**
 * The command palette: Ctrl+K, type a few letters, Enter.
 *
 * <h2>Why a palette and not a bigger menu</h2>
 * The menu is the map, and it stays. This is for the person who already knows
 * where they are going and does not want to travel through two levels of
 * navigation to get there -- which, in a billing portal, is everyone by the
 * second week.
 *
 * <h2>Everything here is also somewhere else</h2>
 * No command is reachable only through the palette. A keyboard shortcut that
 * is the sole route to a feature hides it from everyone who does not know the
 * shortcut exists, which is most people, most of the time.
 */
export function QuickSearch() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((value) => !value)
        return
      }
      // A bare "/" opens it too, the way it does in most tools -- but only
      // when the keystroke is not going into a field. Stealing "/" from
      // somebody typing a document number would be its own bug.
      if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey && !isTyping()) {
        event.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-2 rounded-md border border-line bg-gray-100 px-3 py-2 text-gray-500',
          'transition-colors hover:border-gray-300 hover:text-gray-700',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        )}
        aria-label="Cari halaman atau tindakan"
        aria-keyshortcuts="Control+K"
      >
        <Icon name="search" />
        <span className="hidden text-sm sm:inline">Cari…</span>
        {/* The shortcut is printed on the control, because a shortcut nobody
            is told about is a shortcut nobody uses. */}
        <kbd className="hidden rounded border border-gray-300 bg-card px-1.5 py-0.5 font-sans text-xs text-gray-500 md:inline">
          Ctrl K
        </kbd>
      </button>

      {/* Mounted only while open, so each opening starts with an empty query
          rather than resuming somebody's abandoned search. */}
      {open && <Palette onClose={() => setOpen(false)} />}
    </>
  )
}

/** True when the keystroke belongs to whatever the user is already typing in. */
function isTyping(): boolean {
  const active = document.activeElement as HTMLElement | null
  if (!active) return false
  const tag = active.tagName
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable === true
  )
}

interface Row {
  id: string
  label: string
  group: string
  icon: IconName
  select: () => void
}

function Palette({ onClose }: { onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const navigate = useNavigate()
  const { can, logout } = useAuth()
  const { mintUrl } = getConfig()

  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const commands = useMemo(
    () => buildCommands(can, mintUrl ? { mintUrl } : {}),
    [can, mintUrl],
  )

  const rows = useMemo<Row[]>(() => {
    const matched = rankCommands(commands, query).map<Row>((command) => ({
      id: command.id,
      label: command.label,
      group: command.group,
      icon: command.icon,
      select: () => {
        if (command.href) window.open(command.href, '_blank', 'noopener,noreferrer')
        else if (command.to) navigate(command.to)
        else if (command.run === 'logout') void logout()
      },
    }))

    if (!can(PERM.BILLING_PROFORMA)) return matched

    const jump = documentJump(query)
    if (jump) {
      // First, because a typed number is an unambiguous intent -- nobody types
      // "42" or "PF/01/2609" hoping to land on a settings page.
      return [
        { id: 'jump:proforma', label: jump.label, group: 'Dokumen', icon: 'document', select: () => navigate(jump.to) },
        ...matched,
      ]
    }

    const search = documentSearch(query)
    if (!search) return matched
    return [
      ...matched,
      { id: 'search:proforma', label: search.label, group: 'Dokumen', icon: 'search', select: () => navigate(search.to) },
    ]
  }, [commands, query, can, navigate, logout])

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  // Escape and any other native dismissal route through the same close, so the
  // parent's state can never disagree with what is on screen.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    dialog.addEventListener('close', onClose)
    return () => dialog.removeEventListener('close', onClose)
  }, [onClose])

  // A new query means a new best answer; leaving the cursor on row 4 would let
  // Enter open something the user never looked at.
  useEffect(() => setActive(0), [query])

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active, rows.length])

  function run(index: number) {
    const row = rows[index]
    if (!row) return
    // Close first: navigation unmounts this tree, and a dialog that is still
    // open when its owner disappears leaves the page inert.
    dialogRef.current?.close()
    row.select()
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (rows.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => (index + 1) % rows.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => (index - 1 + rows.length) % rows.length)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActive(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActive(rows.length - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      run(active)
    }
  }

  const activeId = rows[active]?.id

  return (
    <dialog
      ref={dialogRef}
      aria-label="Pencarian cepat"
      // Clicking outside closes it, unlike the confirmation dialogs -- those
      // hold a typed reason worth protecting from a stray click, this holds a
      // search that costs nothing to retype.
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current.close()
      }}
      className={cn(
        'w-[min(36rem,calc(100vw-2rem))] rounded-lg bg-card p-0 text-gray-800 shadow-card-lg',
        // Near the top rather than centred vertically: the list grows
        // downward, and a box that re-centres itself as results appear moves
        // under the cursor. The margins are explicit because Tailwind's
        // preflight zeroes margin on every element, which includes the
        // `margin: auto` the browser uses to place an open dialog.
        'mx-auto mt-[12vh] mb-auto backdrop:bg-black/40 animate-rise',
      )}
    >
      <div className="flex items-center gap-3 border-b border-line px-4">
        <Icon name="search" className="size-5 shrink-0 text-gray-400" />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Cari halaman, tindakan, atau nomor dokumen…"
          className="w-full bg-transparent py-4 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none"
          role="combobox"
          aria-expanded="true"
          aria-controls="quick-search-results"
          aria-activedescendant={activeId}
          aria-autocomplete="list"
        />
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-6 text-gray-500">
          Tidak ada yang cocok dengan “{query}”.
        </p>
      ) : (
        <ul
          id="quick-search-results"
          ref={listRef}
          role="listbox"
          aria-label="Hasil"
          className="max-h-[min(24rem,60vh)] overflow-y-auto py-2"
        >
          {rows.map((row, index) => (
            <li key={row.id}>
              <button
                type="button"
                id={row.id}
                role="option"
                aria-selected={index === active}
                data-active={index === active}
                // Pointer and keyboard drive the same highlight, so the two
                // can never point at different rows at the same time.
                onMouseMove={() => setActive(index)}
                onClick={() => run(index)}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2.5 text-left',
                  index === active ? 'bg-primary-light text-primary' : 'text-gray-700',
                )}
              >
                <Icon name={row.icon} />
                <span className="flex-1 truncate">{row.label}</span>
                <span className="shrink-0 text-xs text-gray-400">{row.group}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-2.5 text-xs text-gray-500">
        <span>↑↓ pilih</span>
        <span>Enter buka</span>
        <span>Esc tutup</span>
      </div>
    </dialog>
  )
}
